import * as React from 'react';

function calculateMaxHeight(
    fullHeight: number,
    bottomPadding: number,
    elementBounds: DOMRect
): number | undefined {
    // distanceToBottom represents the amount of growable space to the bottom of the window,
    // taking into account the minimum amount of padding between the bottom of the element and the bottom
    // of the window
    const distanceToBottom =
        document.documentElement.clientHeight - bottomPadding - elementBounds.top;

    // No distance to travel, so animate instantly
    if (Math.floor(distanceToBottom) <= 0) {
        return undefined;
    }

    // Animate to the full height of the element or to the bottom of the screen if too large
    return Math.min(fullHeight, distanceToBottom);
}

export function useAnimateMaxHeight(
    toggleCollapse: () => void,
    fullHeight: number,
    bottomPadding: number,
    elementRef: React.RefObject<HTMLDivElement>
): [
    maxHeight: number | undefined,
    toggleAnimation: () => void,
    onTransitionEnd: React.TransitionEventHandler<unknown>
] {
    const [maxHeight, setMaxHeight] = React.useState<number | undefined>(undefined);

    const startToggleAnimation = React.useCallback(() => {
        const elementBoundingRect = elementRef.current?.getBoundingClientRect();
        setMaxHeight(
            elementBoundingRect
                ? calculateMaxHeight(fullHeight, bottomPadding, elementBoundingRect)
                : undefined
        );

        requestAnimationFrame(toggleCollapse);
    }, [elementRef, fullHeight, bottomPadding, toggleCollapse]);

    const onTransitionEnd: React.TransitionEventHandler<unknown> = React.useCallback((ev) => {
        if (ev.propertyName === 'max-height') {
            setMaxHeight(undefined);
        }
    }, []);

    return [maxHeight, startToggleAnimation, onTransitionEnd];
}
